import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useDeleteEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InitiativeInclusionNotice from './InitiativeInclusionNotice';

const DeleteNodeDialog = ({ open, onClose, clickedNode, energyGroup }) => {
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const [deleteEnergyTreeNodes] = useDeleteEnergyTreeNodesMutation();

  const inclusions = useMemo(() => {
    const nodesFound = getInitiativeInclusionsForNode(
      clickedNode.energyTreeNodeId,
      energyGroup,
    );
    return nodesFound;
  }, [clickedNode.energyTreeNodeId, energyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(
        ({ viaNode }) =>
          viaNode && (!viaNode.dataStreams || viaNode.dataStreams.length === 0),
      ),
    [inclusions],
  );

  const handleDeleteNode = async () => {
    try {
      await deleteEnergyTreeNodes({
        accountId: account.AccountId,
        energyTreeNodeId: clickedNode.energyTreeNodeId,
      }).unwrap();
      dispatch(showSnackbar({ message: 'Node fjernet', severity: 'success' }));
      onClose();
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i fjernelse af node',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" fontSize="1.25rem">
          {`Sletning af node`}&nbsp;
          {clickedNode?.name}
          <Box
            sx={{
              backgroundColor: getTypeColor(clickedNode?.type),
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              height: 28,
              width: 28,
              justifyContent: 'center',
              fontSize: '14px',
            }}
          >
            <i className={`fa-solid ${getTypeIcon(clickedNode?.type)}`} />
          </Box>
        </Box>

        <i className="fa-regular fa-trash-can"></i>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {t('tree_actions.delete_node_confirmation')}
          </Typography>
        </Box>

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="edit" // or "add"
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={handleDeleteNode} color="error">
          {t('tree_actions.delete_node')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteNodeDialog;
